// Vitals colour coding (nursing-ops-2 #11): one hardcoded standard-ADULT
// threshold map shared by every vitals renderer in the nurse dashboard AND the
// IPD doctor console. Three states: null (normal — keep the default look),
// "amber", "red". Deliberately not configurable and adult-only (paediatric
// ranges are an accepted gap); if thresholds ever move server-side, this map
// is the seed data. Temperature is classified in °F, matching the recorded unit.

// Canonical keys: bp | hr | temp | spo2 | rr
const RANGES = {
  hr: { redLow: 40, amberLow: 50, amberHigh: 100, redHigh: 130 },
  temp: { redLow: 95.0, amberLow: 96.8, amberHigh: 100.3, redHigh: 102.9 },
  spo2: { redLow: 90, amberLow: 95, amberHigh: Infinity, redHigh: Infinity },
  rr: { redLow: 8, amberLow: 12, amberHigh: 20, redHigh: 30 },
  bpSys: { redLow: 90, amberLow: 100, amberHigh: 139, redHigh: 180 },
  bpDia: { redLow: 50, amberLow: 60, amberHigh: 89, redHigh: 110 },
};

const classifyNumber = (n, r) => {
  if (n < r.redLow || n > r.redHigh) return "red";
  if (n < r.amberLow || n > r.amberHigh) return "amber";
  return null;
};

const WORST = { red: 2, amber: 1, null: 0 };

// classifyVital("hr", 135) -> "red" | "amber" | null.
// "bp" accepts "120/80" (classifies the worse of sys/dia). Unparseable or
// missing values return null — never colour what we can't read.
export function classifyVital(key, value) {
  if (value == null || value === "") return null;
  if (key === "bp") {
    const m = String(value).match(/(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/);
    if (!m) return null;
    const sys = classifyNumber(parseFloat(m[1]), RANGES.bpSys);
    const dia = classifyNumber(parseFloat(m[2]), RANGES.bpDia);
    return WORST[sys] >= WORST[dia] ? sys : dia;
  }
  const r = RANGES[key];
  if (!r) return null;
  const n = parseFloat(String(value).replace(/[^\d.-]/g, ""));
  return Number.isNaN(n) ? null : classifyNumber(n, r);
}

// Text colours for plain-text renderers (tables, panel values).
export const VITAL_STATE_COLORS = { red: "#d32f2f", amber: "#ed6c02" };

// sx fragment for a value's Typography/TableCell — {} when normal.
export const vitalValueSx = (key, value) => {
  const state = classifyVital(key, value);
  return state ? { color: VITAL_STATE_COLORS[state], fontWeight: 700 } : {};
};

// MUI Chip color prop for chip renderers — "default" when normal.
export const vitalChipColor = (key, value) => {
  const state = classifyVital(key, value);
  return state === "red" ? "error" : state === "amber" ? "warning" : "default";
};
